import { ApolloProvider } from "@apollo/client";
import { ConfigProvider } from "antd";
import enUSIntl from "antd/lib/locale/en_US";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { Navigate, Route, Routes } from "react-router-dom";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";

import {
  LoginPage,
  PERMISSION_SCOPES,
  PermissionScope,
  TAX_ADVISORY_PERMISSION_SCOPES,
  useAuth,
} from "@/modules/auth";
import { BankTransactionSandbox } from "@/modules/bank-transaction-sandbox";

// v1 imports
import client from "~/api/graphql/client";
import DebugBar from "~/components/common/DebugBar";
import DebugIndicator from "~/components/common/DebugIndicator";
import PageWrapperWithNavigation from "~/components/common/PageWrapperWithNavigation";
import AnnotationTagView from "~/components/pages/AnnotationTagView";
import AnschreibenTemplate from "~/components/pages/Anschreiben/AnschreibenTemplate";
import BelegeView from "~/components/pages/BelegeView";
import BWAView from "~/components/pages/BWAView";
import { CommandHubView } from "~/components/pages/CommandHubView";
import { IntegrationsView } from "@/modules/integrations";
import FeedbackView from "~/components/pages/FeedbackView";
import ActivationView from "~/components/pages/Mandanten/ActivationView";
import OnboardingView from "~/components/pages/Mandanten/OnboardingView";
import RulesRouter from "~/components/pages/RulesView";
import TaxAccountView from "~/components/pages/TaxAccountView";
import TaxDeclaration from "~/components/pages/TaxDeclaration";
import TaxOpsView from "~/components/pages/TaxOpsView";
import TaxServiceView from "~/components/pages/TaxServiceView";
import TransactionView from "~/components/pages/TransactionView";
import VatView from "~/components/pages/VatView";
import ElsterView from "~/components/pages/VatView/ElsterView";
import VerificationView from "~/components/pages/VerificationView";

function App() {
  const { operator, loading } = useAuth();
  const permissionScope = operator?.permissionScope;

  // TODO: refactor auth check move it to a separate file
  const authCheck = (
    Component: React.ComponentType<{}>,
    requiredScope?: PermissionScope[]
  ) => {
    // Check if we're still loading auth state to prevent premature redirects
    if (loading) {
      return <div>Loading...</div>;
    }

    if (!operator) {
      // Check if this redirect is happening due to an explicit logout
      const logoutIntent = sessionStorage.getItem("auth:logoutIntent");
      if (logoutIntent) {
        // Clear the flag immediately after checking
        sessionStorage.removeItem("auth:logoutIntent");
      } else {
        // Only save redirect path if NOT coming from logout and not already on login
        const currentPath = window.location.pathname + window.location.search;
        if (currentPath !== "/login") {
          localStorage.setItem("auth:redirectPath", currentPath);
        }
      }

      return <Navigate to="/login" />;
    }

    // FIXME: Permission scope check is not working as expected
    if (
      permissionScope &&
      requiredScope &&
      !requiredScope.includes(permissionScope)
    ) {
      return <Navigate to="/" />;
    }

    return <Component />;
  };

  return (
    <>
      <DebugBar isSignedIn={!!operator} />
      <DebugIndicator />
      <ApolloProvider client={client}>
        <ConfigProvider locale={enUSIntl}>
          <QueryParamProvider adapter={ReactRouter6Adapter}>
            <PageWrapperWithNavigation>
              <Routes>
                <Route
                  path="/"
                  element={authCheck(ActivationView, [
                    ...TAX_ADVISORY_PERMISSION_SCOPES,
                    PERMISSION_SCOPES.DIY,
                  ])}
                />
                <Route path="/login" element={<LoginPage />} />
                <Route
                  path="/onboarding"
                  element={authCheck(
                    OnboardingView,
                    TAX_ADVISORY_PERMISSION_SCOPES
                  )}
                />
                <Route
                  path="/tax-account-view"
                  element={authCheck(
                    TaxAccountView,
                    TAX_ADVISORY_PERMISSION_SCOPES
                  )}
                />
                <Route
                  path="/tax-service-view"
                  element={authCheck(
                    TaxServiceView,
                    TAX_ADVISORY_PERMISSION_SCOPES
                  )}
                />
                <Route
                  path="/command-hub"
                  element={authCheck(
                    CommandHubView,
                    TAX_ADVISORY_PERMISSION_SCOPES
                  )}
                />
                <Route
                  path="/integrations"
                  element={authCheck(
                    IntegrationsView,
                    TAX_ADVISORY_PERMISSION_SCOPES
                  )}
                />
                <Route
                  path="/bank-transaction-sandbox"
                  element={authCheck(
                    BankTransactionSandbox,
                    TAX_ADVISORY_PERMISSION_SCOPES
                  )}
                />
                <Route
                  path="/transaction-view"
                  element={authCheck(
                    TransactionView,
                    TAX_ADVISORY_PERMISSION_SCOPES
                  )}
                />
                <Route
                  path="/belege-view"
                  element={authCheck(
                    BelegeView,
                    TAX_ADVISORY_PERMISSION_SCOPES
                  )}
                />
                <Route
                  path="/verification-view"
                  element={authCheck(
                    VerificationView,
                    TAX_ADVISORY_PERMISSION_SCOPES
                  )}
                />
                <Route
                  path="/feedback-view"
                  element={authCheck(FeedbackView, [
                    PERMISSION_SCOPES.TAX_SUPERVISOR,
                  ])}
                />
                <Route
                  path="/annotation-tag-view"
                  element={authCheck(AnnotationTagView, [
                    PERMISSION_SCOPES.TAX_SUPERVISOR,
                  ])}
                />
                <Route
                  path="/rules-view/*"
                  element={authCheck(
                    RulesRouter,
                    TAX_ADVISORY_PERMISSION_SCOPES
                  )}
                />
                <Route
                  path="/elster-view"
                  element={authCheck(
                    ElsterView,
                    TAX_ADVISORY_PERMISSION_SCOPES
                  )}
                />
                <Route
                  path="/vat-view"
                  element={authCheck(VatView, TAX_ADVISORY_PERMISSION_SCOPES)}
                />
                <Route
                  path="/bwa-view"
                  element={authCheck(BWAView, TAX_ADVISORY_PERMISSION_SCOPES)}
                />
                <Route
                  path="/tax-declaration/*"
                  element={authCheck(TaxDeclaration, [
                    ...TAX_ADVISORY_PERMISSION_SCOPES,
                    PERMISSION_SCOPES.DIY,
                  ])}
                />
                <Route
                  path="/tax-ops-permission-view"
                  element={authCheck(
                    TaxOpsView,
                    TAX_ADVISORY_PERMISSION_SCOPES
                  )}
                />
                <Route
                  path="/anschreiben-template"
                  element={authCheck(
                    AnschreibenTemplate,
                    TAX_ADVISORY_PERMISSION_SCOPES
                  )}
                />
              </Routes>
            </PageWrapperWithNavigation>
          </QueryParamProvider>
        </ConfigProvider>
      </ApolloProvider>
    </>
  );
}

export default App;
